import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import moment from 'moment';
import axios from 'axios';

import CarContext from './CarContext';

import { getCar, setCarParamsContext } from 'store/actions';
import { APP_PATHS } from 'components/routes/paths';
import { carDetailSelectors } from 'store/selectors';

export const pickupTimeNow = 'now';

function createParams({
	pickupLocation,
	dropOffCity,
	id,
	pickupDate,
	dropDate,
	carDriver,
	unlimitedKm,
	insurance,
	pickupTime
}) {
	const payload = {
		address: pickupLocation ? pickupLocation.address : '',
		lat: pickupLocation ? pickupLocation.lat : '',
		lng: pickupLocation ? pickupLocation.lng : '',
		drop_city_uuid: dropOffCity ? dropOffCity.uuid : '',
		car_id: id,
		pickup_date: moment(pickupDate).format('YYYY-MM-DD'),
		drop_date: moment(dropDate).format('YYYY-MM-DD'),
		car_delivery: 0,
		car_driver: carDriver ? 1 : 0,
		unlimited_km: unlimitedKm ? 1 : 0,
		insurance: insurance ? 1 : 0,
		pickupTime:
			pickupTime === pickupTimeNow
				? moment().format('HH:mm')
				: pickupTime,
		customer_location_lat: null,
		customer_location_lng: null,
		delivery_lat: null,
		delivery_lng: null
	};

	return payload;
}

const CarProvider = ({ children }) => {
	const { id: initId } = useParams();

	const history = useHistory();

	const dispatch = useDispatch();

	const carsSearchParams = useSelector((store) => store.searchCars.params);

	const pickupLocation = carsSearchParams.pickupLocation;

	const { isError, isLoading, item: carDetails, params } = useSelector(
		carDetailSelectors.getAll
	);

	const useContextIfItExists = (fieldName, initValue) =>
		params[fieldName] !== undefined ? params[fieldName] : initValue;

	const id = useContextIfItExists('id', initId);

	const isLoaded = !!carDetails;

	const [pickupDate, setPickupDate] = useState(
		useContextIfItExists(
			'pickupDate',
			moment(carsSearchParams.pickupDate, 'YYYY-MM-DD').toDate()
		)
	);

	const [dropDate, setDropDate] = useState(
		useContextIfItExists(
			'dropDate',
			moment(carsSearchParams.dropDate, 'YYYY-MM-DD').toDate()
		)
	);

	const [insurance, setInsurance] = useState(
		useContextIfItExists('insurance', false)
	);

	const [unlimitedKm, setUnlimitedKm] = useState(
		useContextIfItExists('unlimitedKm', false)
	);

	const [carDriver, setCarDriver] = useState(
		useContextIfItExists('carDriver', false)
	);

	const [pickupTime, setPickupTime] = useState(
		useContextIfItExists('pickupTime', '')
	);

	const [dropOffCity, setDropOffCity] = useState(
		useContextIfItExists('dropOffCity', null)
	);

	const pickupTimeList = useMemo(() => {
		return Object.entries(
			carDetails ? carDetails.pickupTime.time_list : {}
		).map(([value, label]) => ({
			value,
			label
		}));
	}, [carDetails]);

	useEffect(() => {
		const cancelTokenSource = axios.CancelToken.source();
		const cancelToken = cancelTokenSource.token;

		const params = createParams({
			pickupLocation,
			dropOffCity,
			id,
			pickupDate,
			dropDate,
			insurance,
			unlimitedKm,
			carDriver,
			pickupTime
		});
		getCar(params, { cancelToken })(dispatch);

		return () => {
			cancelTokenSource.cancel();
		};
		// eslint-disable-next-line
	}, [
		id,
		pickupDate,
		dropDate,
		insurance,
		unlimitedKm,
		carDriver,
		pickupTime,
		dropOffCity
	]);

	useEffect(() => {
		if (isLoaded) {
			if (pickupTime) {
				const pickUpTimeExists = !!pickupTimeList.find(
					(item) => item.value === pickupTime
				);

				if (!pickUpTimeExists && pickupTime !== pickupTimeNow) {
					// Select first item if current pickupTime doesn't exist in current list
					setPickupTime(
						pickupTimeList.length > 0 ? pickupTimeList[0].value : ''
					);
				}
			} else if (pickupTimeList.length > 0) {
				// Select first item if current pickupTime is not defined
				setPickupTime(pickupTimeList[0].value);
			}
		}
		// eslint-disable-next-line
	}, [isLoaded, pickupTimeList, pickupTime]);

	useEffect(() => {
		if (!isLoaded) {
			return;
		}

		if (!carsSearchParams.dropCity) {
			return;
		}

		const dropCity = carDetails.dropCities.find(
			(city) => city.city_out_uuid === carsSearchParams.dropCity.uuid
		);

		if (!dropCity) {
			setDropOffCity(null);
			return;
		}

		setDropOffCity({
			uuid: dropCity.uuid,
			name: carsSearchParams.dropCity.name
		});
		// eslint-disable-next-line
	}, [isLoaded]);

	const handleChangePickupDate = (value) => setPickupDate(value);
	const handleChangeDropDate = (value) => setDropDate(value);
	const handleChangePickupTime = (value) => setPickupTime(value);
	const handleChangeDropOffCity = (event) =>
		setDropOffCity(event.target.value);
	const handleChangeInsurance = () => setInsurance(!insurance);
	const handleChangeUnlimitedKm = () => setUnlimitedKm(!unlimitedKm);
	const handleChangeCarDriver = () => setCarDriver(!carDriver);

	const handleSetContext = async () => {
		await setCarParamsContext({
			pickupLocation,
			dropOffCity,
			id,
			pickupDate,
			dropDate,
			insurance,
			unlimitedKm,
			carDriver,
			pickupTime
		})(dispatch);
		history.push(APP_PATHS.SIGN_UP);
	};

	return (
		<CarContext.Provider
			value={{
				id,
				pickupLocation,
				pickupDate,
				dropDate,
				insurance,
				unlimitedKm,
				carDriver,
				pickupTime,
				dropOffCity,
				carDetails,
				isLoaded,
				isError,
				isLoading,
				pickupTimeList,
				pickupTimeNow,
				handleChangePickupDate,
				handleChangeDropDate,
				handleChangePickupTime,
				handleChangeDropOffCity,
				handleChangeInsurance,
				handleChangeUnlimitedKm,
				handleChangeCarDriver,
				handleSetContext
			}}
		>
			{children}
		</CarContext.Provider>
	);
};

CarProvider.propTypes = {
	children: PropTypes.node
};

export default CarProvider;
